import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ComisionDispositionResume } from 'src/app/model/other-catalogs-models';
import { CommissionSalePromotionsService } from 'src/app/services/commission-sale-promotions.service';
import { catchError, map, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { NominaService } from 'src/app/services/nomina.service';
import { ReportBaseComision, ReportBaseEmpleado } from 'src/app/model/nomina-models';
import { CustomResponseObject } from 'src/app/model/promotion-models';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

export interface NominaReportRequest {
  fechaEjecucion: string;   // yyyy-MM-dd
  fechaCobro: string;       // yyyy-MM-dd
  periodoInicio: string;    // yyyy-MM-dd HH:mm:ss
  periodoFin: string;       // yyyy-MM-dd HH:mm:ss
  session: string;
}

export interface PagoDistribuidor {
  fechaEjecucion: string;
  fechaCobro: string;
  periodoInicio: string;
  periodoFin: string;
  session: string;
  montoComisiones: number;
  colocacion: number;
  status: string;
}

@Component({
  selector: 'app-pagos-distribuidor',
  templateUrl: './pagos-distribuidor.component.html',
  styleUrls: ['./pagos-distribuidor.component.css']
})
export class PagosDistribuidorComponent implements OnInit {
  loading: boolean = true;

  displayedColumns: string[] = [
    'fechaEjecucion',
    'fechaCobro',
    'periodoInicio',
    'periodoFin',
    'session',
    'montoComisiones',
    'colocacion',
    'status',
    'reportes'
  ];

  dataSource: MatTableDataSource<PagoDistribuidor>;
  searchField: string = 'fechaEjecucion';

  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  constructor(
    private commissionSalePromotionsService: CommissionSalePromotionsService,
    private toastr: ToastrService,
    private nominaService: NominaService
  ) {
    this.dataSource = new MatTableDataSource<PagoDistribuidor>([]);
  }

  ngOnInit() {
    this.cargarDatos();
  }

  ngAfterViewInit() {
    this.setupCustomFilter();
  }

  setupCustomFilter() {
    this.dataSource.filterPredicate = (data: PagoDistribuidor, filter: string) => {
      const searchStr = filter.trim().toLowerCase();

      if (this.searchField === 'fechaEjecucion') {
        return data.fechaEjecucion.toLowerCase().includes(searchStr);
      } else if (this.searchField === 'fechaCobro') {
        return data.fechaCobro.toLowerCase().includes(searchStr);
      }

      return false;
    };
  }

  onSearchFieldChange() {
    // Reconfigura el filtro y reaplica la búsqueda actual
    this.setupCustomFilter();
    if (this.dataSource.filter) {
      this.dataSource.filter = this.dataSource.filter;
    }
  }

  cargarDatos() {
    this.loading = true;

    this.commissionSalePromotionsService.GetCommissionDispositionResume()
      .pipe(
        map((resp: any) => {
          // Transformar cada item del response al formato PagoDistribuidor
          return resp.data.map((item: ComisionDispositionResume) => ({
            fechaEjecucion: item.fechaEjecucion || '',
            fechaCobro: item.FechaCobro || '',
            periodoInicio: this.formatDateTime(item.fechaInicio),
            periodoFin: this.formatDateTime(item.fechaFin),
            session: item.sessionId || '',
            montoComisiones: item.montoTotal || 0,
            colocacion: item.Colocacion || 0,
            status: item.enviado == 0 ? 'NO ENVIADO' : item.enviado == 1 ? 'ENVIADO' : 'INCOMPLETO'
          } as PagoDistribuidor));
        }),
        tap((pagos: PagoDistribuidor[]) => {
          this.dataSource.data = pagos;
          this.loading = false;

          setTimeout(() => {
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
            this.setupCustomFilter();
          });
        }),
        catchError((error) => {
          this.loading = false;
          this.toastr.error('Ocurrió un error al leer el resumen de archivos de envío a pago.');
          return of([]);
        })
      )
      .subscribe();
  }

  formatDateTime(value: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  descargarBaseComision(pago: PagoDistribuidor) {
    console.log('Descargar Base Comisión:', pago);

    // Mostrar loading
    Swal.fire({
      title: 'Consultando reporte',
      text: 'Por favor espere...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const nominaReportRequest: NominaReportRequest = {
      fechaCobro: pago.fechaCobro,
      fechaEjecucion: pago.fechaEjecucion,
      periodoInicio: pago.periodoInicio,
      periodoFin: pago.periodoFin,
      session: pago.session
    };

    this.nominaService.getReportBajasBaseComisionSession(nominaReportRequest).subscribe(
      (resp: CustomResponseObject<ReportBaseComision[]>) => {
        console.log(resp);
        Swal.close();

        if (resp.data && resp.data.length > 0) {
          // Mapear y generar el archivo Excel
          const datosExcel = this.mapearBaseComision(resp.data);
          const nombreArchivo = `Base_Comision_${pago.fechaEjecucion}_${pago.session}`;
          this.generarArchivoExcel(datosExcel, nombreArchivo, 'Base Comisión');

          Swal.fire({
            icon: 'success',
            title: 'Reporte descargado',
            text: 'El reporte se ha generado correctamente',
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: 'warning',
            title: 'Sin datos',
            text: 'No hay datos disponibles para generar el reporte',
            confirmButtonText: 'Aceptar'
          });
        }
      },
      (error) => {
        console.error(error);
        Swal.close();

        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Ocurrió un error al generar el reporte',
          confirmButtonText: 'Aceptar'
        });
      }
    );
  }

  descargarBaseEmpleado(pago: PagoDistribuidor) {
    console.log('Descargar Base Empleado:', pago);

    // Mostrar loading
    Swal.fire({
      title: 'Consultando reporte',
      text: 'Por favor espere...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const nominaReportRequest: NominaReportRequest = {
      fechaCobro: pago.fechaCobro,
      fechaEjecucion: pago.fechaEjecucion,
      periodoInicio: pago.periodoInicio,
      periodoFin: pago.periodoFin,
      session: pago.session
    };

    this.nominaService.getReportBajasBaseEmpleadoSession(nominaReportRequest).subscribe(
      (resp: CustomResponseObject<ReportBaseEmpleado[]>) => {
        console.log(resp);
        Swal.close();

        if (resp.data && resp.data.length > 0) {
          // Mapear y generar el archivo Excel
          const datosExcel = this.mapearBaseEmpleado(resp.data);
          const nombreArchivo = `Base_Empleado_${pago.fechaEjecucion}_${pago.session}`;
          this.generarArchivoExcel(datosExcel, nombreArchivo, 'Base Empleado');

          Swal.fire({
            icon: 'success',
            title: 'Reporte descargado',
            text: 'El reporte se ha generado correctamente',
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: 'warning',
            title: 'Sin datos',
            text: 'No hay datos disponibles para generar el reporte',
            confirmButtonText: 'Aceptar'
          });
        }
      },
      (error) => {
        console.error(error);
        Swal.close();

        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Ocurrió un error al generar el reporte',
          confirmButtonText: 'Aceptar'
        });
      }
    );
  }

  onReporteChange(tipo: string, pago: PagoDistribuidor, selectElement: any) {
    if (tipo === 'comision') {
      this.descargarBaseComision(pago);
    } else if (tipo === 'empleado') {
      this.descargarBaseEmpleado(pago);
    }

    setTimeout(() => {
      selectElement.value = '';
    }, 100);
  }

  private generarArchivoExcel(data: any[], nombreArchivo: string, nombreHoja: string) {
    // Crear un nuevo libro de trabajo
    const wb: XLSX.WorkBook = XLSX.utils.book_new();

    // Convertir los datos a una hoja de trabajo
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);

    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, nombreHoja);

    // Generar el archivo y descargarlo
    XLSX.writeFile(wb, `${nombreArchivo}.xlsx`);
  }

  private mapearBaseComision(datos: ReportBaseComision[]): any[] {
    return datos.map(item => ({
      'DAP': item.dap || '',
      'Fecha Cobro': item.fechaCobro || '',
      'Fecha Liberación': item.fechaLiberacion || '',
      'Año': item.year || 0,
      'Mes': item.month || 0,
      'Día': item.day || 0,
      'Capital': item.capital || 0,
      'Colectivo': item.colectivo || '',
      'Folio Solicitud': item.folioSolicitud || '',
      'Sucursal': item.sucursal || '',
      'Estado': item.estado || '',
      'Zona': item.zona || '',
      'Producto': item.producto || '',
      'Comisión': item.comision || 0,
      'Asesor ID': item.asesorId || '',
      'Asesor Nombre': item.asesorName || '',
      'Distribuidor': item.distribuidor || '',
      'Inter Comisión': item.interComision || 0,
      'Inter Monto Comisión': item.interMontoComision || 0,
      'Inter IVA': item.interIVA || 0,
      'Inter Total': item.interTotal || 0,
      'Inter Factura': item.interFactura || 0,
      'Promo Comisión': item.promoComision || 0,
      'Promo Monto Comisión': item.promoMontoComision || 0,
      'Promo IVA': item.promoIVA || 0,
      'Promo Total': item.promoTotal || 0,
      'Promo Factura': item.promoFactura || 0
    }));
  }

  private mapearBaseEmpleado(datos: ReportBaseEmpleado[]): any[] {
    return datos.map(item => ({
      'Mes': item.mes || '',
      'Venta': item.venta || '',
      'Pago': item.pago || '',
      'Distribuidor': item.distribuidor || '',
      'Tipo Comisión': item.tipoComision || '',
      'Asesor ID': item.asesorId || '',
      'Asesor Nombre': item.asesorNombre || '',
      'Dirección': item.direccion || '',
      'Entidad': item.entidad || '',
      'Sucursal': item.sucursal || '',
      'Departamento': item.departamento || '',
      'Puesto': item.puesto || '',
      'Colocación': item.colocacion || 0,
      'Monto Comisión': item.montoComision || 0
    }));
  }
}